/* Rhody Reviews api/v1 -- requests domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';

/* -------------------------------------------------------------------
   Cadence / request-scheduling preferences -- GET/PUT
   /api/v1/cadence-preferences. Matches the engine endpoints in
   api/v1/app.py + the resolver in agents/cadence_prefs.py.

   - track_a_delay_minutes: initial-request timing (minutes after a job).
   - track_b_max_touches:   number of follow-up touches (hard cap 3).
   - track_b_min_spacing_days: minimum days between follow-up touches.
   - paused: kill-switch -- when true the engine sends NOTHING.

   `saved` is the stored override (nulls = "use config default"); `effective`
   is the post-clamp value the engine actually runs; `limits` documents the
   clamp range for building a valid selector.
   ------------------------------------------------------------------- */
export interface CadenceSaved {
  track_a_delay_minutes: number | null;
  track_b_max_touches: number | null;
  track_b_min_spacing_days: number | null;
  paused: boolean;
}

export interface CadenceEffective {
  track_a_delay_minutes: number;
  track_b_max_touches: number;
  track_b_min_spacing_days: number;
  paused: boolean;
}

export interface CadenceLimits {
  track_a_delay_minutes: { min: number; max: number };
  track_b_max_touches: { min: number; max: number };
  track_b_min_spacing_days: { min: number };
}

export interface CadencePreferencesResponse {
  client_id: string;
  defaulted: boolean;
  saved: CadenceSaved;
  effective: CadenceEffective;
  limits: CadenceLimits;
}

export interface CadencePreferencesWriteResponse {
  client_id: string;
  saved: CadenceSaved;
  effective: CadenceEffective;
}

/* Any subset is accepted. An integer knob set to null clears the override
   (back to config default); paused is a plain boolean. */
export interface CadencePreferencesPatch {
  track_a_delay_minutes?: number | null;
  track_b_max_touches?: number | null;
  track_b_min_spacing_days?: number | null;
  paused?: boolean;
}

/* -------------------------------------------------------------------
   This-period review-request funnel -- GET /api/v1/requests/summary?days=30.
   Matches requests_summary() in engine/api/v1/app.py. `source` says which
   path produced the numbers: "requests" (the requests table) or
   "transactions" (the fallback derived from txn ask/received timestamps).
   When source is "transactions", delivered_or_opened is honestly 0 (no
   per-message delivery signal exists) -- the UI must render that truthfully.
   An empty period returns all-zero counts (never fabricated).
   ------------------------------------------------------------------- */
export interface RequestsFunnel {
  sent: number;
  delivered_or_opened: number;
  left_review: number;
}

export interface RequestsSummary {
  client_id: string;
  period_days: number;
  since: string;
  source: 'requests' | 'transactions';
  funnel: RequestsFunnel;
  as_of: string;
}

/* One recent review-request row for the Requests screen (GET /requests).
   Status-tracker fields (sms_status/email_status/stage/touch_number) are
   computed server-side; SMS is dry-run/gated so its status is labelled
   honestly (e.g. "Dry-run (queued)"), never "Sent". */
export interface RequestRow {
  id: string;
  customer_id: string | null;
  customer_name: string;
  channel: 'sms' | 'email' | string | null;
  status: string | null;
  sms_status: string;
  email_status: string;
  sent_at: string | null;
  last_touch_at: string | null;
  touch_number: number | null;
  total_touches: number;
  stage: string | null;
  created_at: string | null;
}

export interface RequestsResponse {
  client_id: string;
  count: number;
  total_touches: number;
  requests: RequestRow[];
}

/* One created row's per-channel send result. `transmitted` is true only when
   the email actually left via Brevo; `reason` carries the honest queued reason
   (e.g. 'email_disabled', 'no_review_link', 'no_customer_email',
   'sms_disabled', 'tenant_sms_not_authorized', 'no_customer_phone',
   'no_consumer_consent', 'outside_local_window') when it did not. */
export interface RequestSendResult {
  transmitted: boolean;
  reason: string | null;
  message_id?: string | null;
}

/* POST /api/v1/requests -> created review-request record(s). EMAIL is live:
   `sent_count` counts rows that actually transmitted, and `dry_run` is true
   only when nothing on this channel sent. The per-row `send` result lets the UI
   show the TRUE status per customer instead of a blanket "queued" reason. */
export interface RequestsCreated {
  client_id: string;
  created_count: number;
  sent_count: number;
  /* How many customers were enrolled into the SMART 3-touch sequence (Day
     0/3/7). Usually == created_count; lower only if enrollment could not be
     recorded (never blocks the send). */
  enrolled_count?: number;
  channel: 'sms' | 'email' | string;
  created: {
    id: string;
    customer_id: string;
    status: string | null;
    send?: RequestSendResult;
  }[];
  skipped_count: number;
  skipped: { customer_id: string; reason: string }[];
  dry_run: boolean;
  /* The tenant's daily send allowance + what is left after this batch, so the
     UI can be honest about how many went now vs held by the 25/day cap. */
  daily_cap?: number;
  daily_cap_remaining?: number;
}

/* POST /api/v1/requests/test-send -> the onboarding "send yourself a test
   review request" result. Card-FREE + self-scoped: the destination is the
   owner's own inbox; the review link + business name come from the tenant. On
   an invalid email the backend 400s (surfaced as an ApiError); otherwise it
   returns 200 with sent true/false + a reason. */
export interface TestSendResult {
  sent: boolean;
  to?: string;
  channel?: string;
  /* True when the tenant has not yet confirmed Google, so an example link was
     used. The UI can note "connect Google to use your real link". */
  link_is_placeholder?: boolean;
  message_id?: string | null;
  /* Present when sent is false (e.g. 'email_unavailable', 'send_failed'). */
  reason?: string;
  detail?: string;
}

export const requestsApi = {
  /* Send ONE sample review-request EMAIL to the owner's own inbox (onboarding
     chute step 3). Optional to_email/first_name; both default server-side to
     the signed-in owner's contact fields. */
  testSend(input: { to_email?: string; first_name?: string } = {}): Promise<TestSendResult> {
    return request<TestSendResult>('/api/v1/requests/test-send', {
      method: 'POST',
      body: input,
    });
  },

  /* Recent review-request rows for the Requests screen (newest first). */
  listRequests(
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<RequestsResponse> {
    return request<RequestsResponse>('/api/v1/requests', { query: params, signal });
  },

  /* Create review-request record(s) for one or more customers. Fires ONLY on
     an explicit operator click. The records are PERSISTED (status queued) but
     NO SMS/email is sent -- live sending is gated until Twilio is approved
     (response.dry_run is true). */
  createRequests(input: {
    customer_ids: string[];
    channel?: 'sms' | 'email';
    subject?: string;
    message?: string;
  }): Promise<RequestsCreated> {
    return request<RequestsCreated>('/api/v1/requests', {
      method: 'POST',
      body: input,
    });
  },

  /* This-period review-request funnel for the Dashboard. days (server
     default 30, max 365) sets the look-back. source tells you whether the
     numbers came from the requests table or the transactions fallback; when
     "transactions", delivered_or_opened is honestly 0. */
  getRequestsSummary(days?: number, signal?: AbortSignal): Promise<RequestsSummary> {
    return request<RequestsSummary>('/api/v1/requests/summary', {
      query: { days },
      signal,
    });
  },

  /* Read the client's cadence (request-scheduling) preferences: the saved
     overrides, the effective post-clamp values the engine will run, the
     clamp limits, and the pause flag. GET /api/v1/cadence-preferences. */
  getCadencePreferences(signal?: AbortSignal): Promise<CadencePreferencesResponse> {
    return request<CadencePreferencesResponse>('/api/v1/cadence-preferences', {
      signal,
    });
  },

  /* Save a subset of cadence knobs + the pause toggle. Integer knobs are
     clamped server-side to Penn's compliance caps (never rejected); the
     response's `effective` block shows the post-clamp truth. PUT
     /api/v1/cadence-preferences. */
  updateCadencePreferences(
    patch: CadencePreferencesPatch,
  ): Promise<CadencePreferencesWriteResponse> {
    return request<CadencePreferencesWriteResponse>('/api/v1/cadence-preferences', {
      method: 'PUT',
      body: patch,
    });
  },
};
